import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import translate from 'core/translate'
import { canvasPresets } from '../../templateEditor.data'
import { defaultEditorTheme } from '../../templateEditor.theme'
import { CanvasBgMedia, CanvasPresetId, TemplateElement } from '../../templateEditor.types'
import { clampCanvasSize, clampRectToCanvas } from '../../templateEditor.utils'
import { useTemplateEditorHistory } from './useTemplateEditorHistory'

const theme = defaultEditorTheme

type HistoryApi = ReturnType<typeof useTemplateEditorHistory>

type Params = {
    setElements: Dispatch<SetStateAction<TemplateElement[]>>
    setSelectedIds: Dispatch<SetStateAction<string[]>>
    history: HistoryApi
}

export const useTemplateEditorCanvas = ({ setElements, setSelectedIds, history }: Params) => {
    const [canvasW, setCanvasW] = useState(theme.defaultCanvasW)
    const [canvasH, setCanvasH] = useState(theme.defaultCanvasH)
    const [canvasBg, setCanvasBg] = useState(theme.defaultCanvasBg)
    const [canvasBgMedia, setCanvasBgMedia] = useState<CanvasBgMedia | null>(null)
    const [showGrid, setShowGrid] = useState(false)
    const [gridSize, setGridSize] = useState(theme.defaultGridSize)

    const presetId: CanvasPresetId = useMemo(() => {
        const match = canvasPresets.find((preset) => preset.w === canvasW && preset.h === canvasH)
        return match ? match.id : 'custom'
    }, [canvasW, canvasH])

    const clampAllElements = useCallback(
        (nextW: number, nextH: number) => {
            setElements((prev) =>
                prev.map((element) => ({
                    ...element,
                    ...clampRectToCanvas(element, nextW, nextH, theme.minElSize),
                }))
            )
        },
        [setElements]
    )

    const setPreset = useCallback(
        (id: CanvasPresetId) => {
            const preset = canvasPresets.find((item) => item.id === id)
            if (!preset) {
                return
            }

            setCanvasW(preset.w)
            setCanvasH(preset.h)
            clampAllElements(preset.w, preset.h)
        },
        [clampAllElements]
    )

    const swapOrientation = useCallback(() => {
        setCanvasW(canvasH)
        setCanvasH(canvasW)
        clampAllElements(canvasH, canvasW)
    }, [canvasH, canvasW, clampAllElements])

    const setCanvasSize = useCallback(
        (w: number, h: number) => {
            const nextW = clampCanvasSize(w, theme)
            const nextH = clampCanvasSize(h, theme)
            setCanvasW(nextW)
            setCanvasH(nextH)
            clampAllElements(nextW, nextH)
        },
        [clampAllElements]
    )

    const clearCanvas = useCallback(() => {
        if (!window.confirm(translate('templateEditorClearCanvasConfirm'))) {
            return
        }
        history.pushHistory()
        setElements([])
        setSelectedIds([])
    }, [history, setElements, setSelectedIds])

    const setCanvasBackgroundMedia = useCallback((media: CanvasBgMedia | null) => {
        setCanvasBgMedia(media)
        if (media?.fileType === 'image') {
            setCanvasBg('transparent')
            return
        }

        if (!media) {
            setCanvasBg(theme.defaultCanvasBg)
        }
    }, [])

    const hydrateCanvas = useCallback(
        (next: {
            w: number
            h: number
            bg: string
            bgMedia: CanvasBgMedia | null
            showGrid: boolean
            gridSize: number
        }) => {
            setCanvasW(next.w)
            setCanvasH(next.h)
            setCanvasBg(next.bg)
            setCanvasBgMedia(next.bgMedia)
            setShowGrid(next.showGrid)
            setGridSize(next.gridSize)
        },
        []
    )

    return {
        canvasW,
        canvasH,
        canvasBg,
        canvasBgMedia,
        showGrid,
        gridSize,
        presetId,
        setCanvasBg,
        setCanvasBackgroundMedia,
        setShowGrid,
        setGridSize,
        setPreset,
        setCanvasSize,
        swapOrientation,
        clearCanvas,
        hydrateCanvas,
    }
}
